import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  GraduationCap, ArrowLeft, Search, Loader2, CheckCircle, Globe
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api/client';

const UniversityRequest = () => {
  const [formData, setFormData] = useState({
    university_name: '',
    country: '',
    city: '',
    website: '',
    reason: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.university_name.trim()) {
      toast({ title: 'Error', description: 'Please enter a university name.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/university-hub/request', formData);
      setSubmitted(true);
      toast({ title: 'Request Submitted', description: 'We will review your university request and add it to our platform.' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to submit request. Please try again.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4" data-testid="university-request-success">
        <Card className="max-w-lg w-full bg-white shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for requesting <strong>{formData.university_name}</strong>. Our team will review it and work on adding it to the platform.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/colleges">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
                  Browse Colleges
                </Button>
              </Link>
              <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({ university_name: '', country: '', city: '', website: '', reason: '' }); }}>
                Request Another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="university-request-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors" data-testid="back-to-home-link">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-2xl mb-4">
              <GraduationCap className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Request a University</h1>
            <p className="text-white/80">Can't find your dream university? Let us know and we'll add it!</p>
            <Badge className="mt-3 bg-white/20 text-white border-0">
              <Globe className="h-3 w-3 mr-1" /> Now Accepting Requests
            </Badge>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 -mt-8 pb-16">
        <Card className="bg-white shadow-2xl border-0 overflow-hidden">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="university-request-form">
              <div className="space-y-2">
                <Label htmlFor="university_name">University Name *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="university_name"
                    name="university_name"
                    placeholder="e.g., University of Toronto"
                    value={formData.university_name}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="university-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="country"
                    placeholder="e.g., Canada"
                    value={formData.country}
                    onChange={handleChange}
                    data-testid="country-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    placeholder="e.g., Toronto"
                    value={formData.city}
                    onChange={handleChange}
                    data-testid="city-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">University Website (optional)</Label>
                <Input
                  id="website"
                  name="website"
                  placeholder="https://www.university.edu"
                  value={formData.website}
                  onChange={handleChange}
                  data-testid="website-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Why do you want this university added? (optional)</Label>
                <textarea
                  id="reason"
                  name="reason"
                  placeholder="I'm interested in applying to their Computer Science program..."
                  value={formData.reason}
                  onChange={handleChange}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="reason-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-5"
                disabled={loading}
                data-testid="submit-request-button"
              >
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Request'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UniversityRequest;
